import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, FunnelChart, Funnel, LabelList
} from 'recharts';
import {
  Brain, TrendingUp, ShoppingCart, Users, Eye, Search,
  Clock, ArrowRight, ChevronRight, RefreshCw, X, AlertTriangle,
  MousePointer, LogIn, Package, CreditCard, XCircle
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';

/* ── Action icon/label map ─────────────────────────── */
const ACTION_MAP = {
  page_view: { icon: Eye, label: 'Vista de pagina', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  search: { icon: Search, label: 'Busqueda', color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
  add_to_cart: { icon: ShoppingCart, label: 'Agrego al carrito', color: 'text-green-400', bg: 'bg-green-500/20' },
  remove_from_cart: { icon: XCircle, label: 'Quito del carrito', color: 'text-red-400', bg: 'bg-red-500/20' },
  begin_checkout: { icon: CreditCard, label: 'Inicio checkout', color: 'text-amber-400', bg: 'bg-amber-500/20' },
  checkout: { icon: CreditCard, label: 'Compra finalizada', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  login: { icon: LogIn, label: 'Inicio sesion', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  product_view: { icon: Package, label: 'Vio producto', color: 'text-orange-400', bg: 'bg-orange-500/20' },
};

const fallbackAction = { icon: MousePointer, label: 'Accion', color: 'text-gray-400', bg: 'bg-gray-500/20' };

const PIE_COLORS = ['#f97316', '#6b7280'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 shadow-lg">
      <p className="text-gray-300 text-xs mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm" style={{ color: p.color || p.fill }}>
          {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('ingreso')
            ? formatPrice(p.value) : p.value?.toLocaleString?.() ?? p.value}
        </p>
      ))}
    </div>
  );
};

const getDefaultDates = () => {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 30);
  return { start: s.toISOString().slice(0, 10), end: e.toISOString().slice(0, 10) };
};

const AdminIntelligence = () => {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [abandonedCount, setAbandonedCount] = useState(0);
  const [funnel, setFunnel] = useState(null);
  const [sessionDaily, setSessionDaily] = useState([]);

  // Timeline modal
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [timelineSessionId, setTimelineSessionId] = useState('');
  const [timelineLoading, setTimelineLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        adminAPI.getDashboardStats(),
        adminAPI.getDashboardTimeseries(startDate, endDate),
        adminAPI.getSessions(50),
        adminAPI.getAbandonedCarts(),
        adminAPI.getAbandonedCartsCount(),
        adminAPI.getIntelligenceFunnel(),
      ]);
      if (results[0].status === 'fulfilled') setStats(results[0].value);
      if (results[1].status === 'fulfilled') setSeries(results[1].value?.series || []);
      if (results[2].status === 'fulfilled') setSessions(results[2].value?.sessions || []);
      if (results[3].status === 'fulfilled') setAbandonedCarts(results[3].value || []);
      if (results[4].status === 'fulfilled') setAbandonedCount(results[4].value?.count ?? 0);
      if (results[5].status === 'fulfilled') {
        const f = results[5].value;
        setFunnel(f);
        setAbandonedCount(prev => f?.abandoned_count ?? prev);
        setSessionDaily(f?.session_daily || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const openTimeline = async (sessionId) => {
    if (!sessionId) return;
    setTimelineSessionId(sessionId);
    setTimelineOpen(true);
    setTimelineLoading(true);
    try {
      const data = await adminAPI.getSessionTimeline(sessionId);
      setTimelineData(data || []);
    } catch {
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  /* ── Funnel data (real) ─────────────────────────── */
  const funnelData = useMemo(() => {
    const totalUsers = funnel?.total_users || stats?.users?.total || 0;
    const totalCarts = funnel?.total_carts || 0;
    const totalOrders = funnel?.total_orders || stats?.orders?.total || 0;
    if (totalUsers === 0 && totalCarts === 0 && totalOrders === 0) return [];
    return [
      { name: 'Usuarios', value: totalUsers, fill: '#3b82f6' },
      { name: 'Carritos', value: totalCarts, fill: '#f59e0b' },
      { name: 'Compras', value: totalOrders, fill: '#22c55e' },
    ];
  }, [stats, funnel]);

  /* ── Pie data: abandoned vs completed (real) ──── */
  const pieData = useMemo(() => {
    const completed = funnel?.total_orders || stats?.orders?.total || 0;
    const abandoned = abandonedCount || 0;
    if (completed === 0 && abandoned === 0) return [];
    return [{ name: 'Completados', value: completed }, { name: 'Abandonados', value: abandoned }];
  }, [stats, abandonedCount, funnel]);

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-orange-500/20">
              <Brain className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="intelligence-title">Inteligencia</h1>
              <p className="text-gray-500 text-sm">Analisis cruzado de sesiones, ingresos y conversion</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white w-36" />
            <span className="text-gray-500">—</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white w-36" />
            <Button onClick={load} variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-700">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* ══════════ 4 CHART GRID ══════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* 1. Ingresos y Pedidos (BarChart) */}
          <Card className="bg-gray-800 border-gray-700 p-5" data-testid="chart-revenue-orders">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-orange-400" /> Ingresos y Pedidos
            </h3>
            <div className="h-60">
              {series.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis yAxisId="left" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: '#9CA3AF', fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="revenue" name="Ingresos" fill="#f97316" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="right" dataKey="orders" name="Pedidos" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">Recopilando datos de ingresos...</div>
              )}
            </div>
          </Card>

          {/* 2. Trafico de Sesiones (LineChart) */}
          <Card className="bg-gray-800 border-gray-700 p-5" data-testid="chart-traffic">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" /> Trafico de Sesiones
            </h3>
            <div className="h-60">
              {sessionDaily.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={sessionDaily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ color: '#9CA3AF', fontSize: 12 }} />
                  <Line type="monotone" dataKey="sesiones" name="Sesiones" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="nuevos" name="Nuevos" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">Recopilando datos de trafico...</div>
              )}
            </div>
          </Card>

          {/* 3. Embudo de Conversion */}
          <Card className="bg-gray-800 border-gray-700 p-5" data-testid="chart-funnel">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-green-400" /> Embudo de Conversion
            </h3>
            <div className="flex items-center justify-center h-60">
              {funnelData.length > 0 ? (
              <div className="flex items-end gap-6 h-full py-4 w-full">
                {funnelData.map((item, i) => {
                  const maxVal = funnelData[0].value || 1;
                  const pct = Math.max((item.value / maxVal) * 100, 8);
                  const convRate = i > 0 ? ((item.value / funnelData[i - 1].value) * 100).toFixed(1) : null;
                  return (
                    <div key={item.name} className="flex flex-col items-center gap-2 flex-1">
                      <div className="flex-1 w-full flex flex-col justify-end">
                        <div
                          className="w-full rounded-t-lg transition-all relative group"
                          style={{ height: `${pct}%`, background: item.fill, minHeight: 30 }}
                        >
                          <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-white font-bold text-sm">
                            {item.value.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 font-medium text-center">{item.name}</p>
                      {convRate && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-gray-300">
                          {convRate}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">Recopilando datos de conversion...</div>
              )}
            </div>
          </Card>

          {/* 4. Carritos Abandonados (PieChart) */}
          <Card className="bg-gray-800 border-gray-700 p-5" data-testid="chart-abandoned">
            <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" /> Tasa de Carritos Abandonados
            </h3>
            <div className="h-60 flex items-center">
              {pieData.length > 0 ? (<>
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i]} stroke="transparent" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3 pl-2">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm" style={{ background: PIE_COLORS[0] }} />
                    <span className="text-sm text-gray-300">Completados</span>
                  </div>
                  <p className="text-2xl font-bold text-white ml-5">{stats?.orders?.total || 0}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-sm" style={{ background: PIE_COLORS[1] }} />
                    <span className="text-sm text-gray-300">Abandonados</span>
                  </div>
                  <p className="text-2xl font-bold text-white ml-5">{abandonedCount}</p>
                </div>
                <p className="text-xs text-gray-500 ml-5">
                  Tasa: {pieData[0].value + pieData[1].value > 0
                    ? ((pieData[1].value / (pieData[0].value + pieData[1].value)) * 100).toFixed(1)
                    : 0}%
                </p>
              </div>
              </>) : (
                <div className="flex items-center justify-center w-full h-full text-gray-500 text-sm">Recopilando datos de carritos...</div>
              )}
            </div>
          </Card>
        </div>

        {/* ══════════ SESSIONS TABLE ══════════ */}
        <Card className="bg-gray-800 border-gray-700" data-testid="sessions-table">
          <div className="p-5 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-400" /> Sesiones Recientes
            </h3>
            <span className="text-xs text-gray-500">{sessions.length} sesiones</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-3 text-gray-400 font-medium">Session ID</th>
                  <th className="text-left p-3 text-gray-400 font-medium">Usuario</th>
                  <th className="text-center p-3 text-gray-400 font-medium">Acciones</th>
                  <th className="text-left p-3 text-gray-400 font-medium">Ultima accion</th>
                  <th className="text-left p-3 text-gray-400 font-medium">Primera vez</th>
                  <th className="text-center p-3 text-gray-400 font-medium">Timeline</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.session_id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="p-3 font-mono text-xs text-gray-300">{s.session_id?.slice(0, 12)}...</td>
                    <td className="p-3 text-gray-300">{s.user_id ? s.user_id.slice(0, 10) + '...' : <span className="text-gray-500 italic">Anonimo</span>}</td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold">{s.actions}</span>
                    </td>
                    <td className="p-3 text-gray-300 text-xs">{s.last_action}</td>
                    <td className="p-3 text-gray-500 text-xs">{s.first_seen ? new Date(s.first_seen).toLocaleString('es-MX') : '—'}</td>
                    <td className="p-3 text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                        onClick={() => openTimeline(s.session_id)}
                        data-testid={`timeline-btn-${s.session_id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Ver
                      </Button>
                    </td>
                  </tr>
                ))}
                {sessions.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-gray-500">Sin sesiones registradas</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ══════════ ABANDONED CARTS TABLE ══════════ */}
        <Card className="bg-gray-800 border-gray-700" data-testid="abandoned-carts-table">
          <div className="p-5 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <ShoppingCart className="w-4 h-4 text-amber-400" /> Carritos Abandonados
            </h3>
            <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 font-bold">{abandonedCount}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-3 text-gray-400 font-medium">Usuario</th>
                  <th className="text-left p-3 text-gray-400 font-medium">Email</th>
                  <th className="text-center p-3 text-gray-400 font-medium">Items</th>
                  <th className="text-right p-3 text-gray-400 font-medium">Total</th>
                  <th className="text-left p-3 text-gray-400 font-medium">Actualizado</th>
                  <th className="text-center p-3 text-gray-400 font-medium">Auditoria</th>
                </tr>
              </thead>
              <tbody>
                {abandonedCarts.map((c, i) => (
                  <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="p-3 text-gray-300">{c.user_name || '—'}</td>
                    <td className="p-3 text-gray-400 text-xs">{c.user_email || '—'}</td>
                    <td className="p-3 text-center text-gray-300">{c.items?.length || 0}</td>
                    <td className="p-3 text-right text-white font-mono">{formatPrice(c.total || 0)}</td>
                    <td className="p-3 text-gray-500 text-xs">{c.updated_at ? new Date(c.updated_at).toLocaleString('es-MX') : '—'}</td>
                    <td className="p-3 text-center">
                      {c.session_id ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                          onClick={() => openTimeline(c.session_id)}
                          data-testid={`audit-cart-${i}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> Auditar
                        </Button>
                      ) : (
                        <span className="text-gray-600 text-xs">Sin session</span>
                      )}
                    </td>
                  </tr>
                ))}
                {abandonedCarts.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-gray-500">Sin carritos abandonados</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ══════════ TIMELINE MODAL ══════════ */}
      <Dialog open={timelineOpen} onOpenChange={setTimelineOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-orange-400" />
              Auditoria de Sesion
            </DialogTitle>
            <p className="text-xs text-gray-500 font-mono mt-1">{timelineSessionId}</p>
          </DialogHeader>

          {timelineLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-orange-500 animate-spin" />
            </div>
          ) : timelineData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Eye className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Sin eventos registrados para esta sesion</p>
            </div>
          ) : (
            <div className="relative pl-8 py-2 space-y-0">
              {/* Vertical line */}
              <div className="absolute left-[15px] top-4 bottom-4 w-px bg-gray-600" />

              {timelineData.map((event, idx) => {
                const mapped = ACTION_MAP[event.action] || fallbackAction;
                const Icon = mapped.icon;
                const ts = new Date(event.timestamp);
                const timeStr = ts.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                const meta = event.metadata || {};

                return (
                  <div key={idx} className="relative flex items-start gap-3 pb-5" data-testid={`timeline-event-${idx}`}>
                    {/* Dot on the line */}
                    <div className={`absolute -left-8 top-1 w-[18px] h-[18px] rounded-full ${mapped.bg} border-2 border-gray-800 flex items-center justify-center z-10`}>
                      <Icon className={`w-2.5 h-2.5 ${mapped.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 bg-gray-700/40 rounded-lg p-3 hover:bg-gray-700/60 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${mapped.color}`}>{mapped.label}</span>
                        <span className="text-[10px] text-gray-500 font-mono">{timeStr}</span>
                      </div>
                      {Object.keys(meta).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-2">
                          {Object.entries(meta).map(([k, v]) => (
                            <span key={k} className="text-[10px] px-2 py-0.5 rounded bg-gray-600/60 text-gray-300">
                              {k}: <span className="text-white font-medium">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Arrow connector (except last) */}
                    {idx < timelineData.length - 1 && (
                      <div className="absolute -left-[23px] bottom-0 text-gray-600">
                        <ArrowRight className="w-3 h-3 rotate-90" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminIntelligence;
